'use client';

import { useEffect, useRef, useState } from 'react';

interface IProps {
  link: string;
  originDuration?: number;
  defaultSpeed?: string;
  muted?: boolean;
  volume?: number;
  autoPlay?: boolean;
  callback?: {
    handleSeekTimeCb?: (time: number) => void;
    handlePlayStatusChangeCb?: (isPlaying: boolean) => void;
    handleVolumeChangeCb?: (volume: number) => void;
    handlePlayEndCb?: () => void;
  };
}

interface IUseAudio {
  audioRef: React.MutableRefObject<HTMLAudioElement>; // 播放用 audio（对外保持兼容）
  currentTime: number;
  speed: string;
  duration: number;
  isPlaying: boolean;
  loading: boolean;
  audioVolume: number;
  handlePlayStatusChange: () => void;
  handleSpeedChange: (value: string) => void;
  handleSeekTime: (time: number) => void;
  handleReplay: () => void;
}

function useAudio(props: IProps): IUseAudio {
  const {
    link,
    defaultSpeed = '1.0',
    muted = false,
    volume = 1,
    autoPlay = false,
    originDuration = 0,
    callback
  } = props;

  const { handleSeekTimeCb, handlePlayStatusChangeCb, handleVolumeChangeCb, handlePlayEndCb } =
    callback || {};

  // 播放用 audio（给用户听）
  const playerRef = useRef<HTMLAudioElement>(new Audio());
  // 分析用 audio（接到 AudioContext，不出声）
  const analyserAudioRef = useRef<HTMLAudioElement>(new Audio());

  // 对外暴露的 audioRef，保持和之前兼容：指向播放用 audio
  const audioRef = playerRef;

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const lockPlayRef = useRef<boolean>(false);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [speed, setSpeed] = useState<string>(defaultSpeed);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(originDuration);
  const [audioVolume, setAudioVolume] = useState(0);

  // -----------------------
  // 核心：初始化 / 销毁逻辑
  // -----------------------

  // 播放器事件处理 - 全部挂在 playerRef 上
  const handleOnTimeUpdate = () => {
    setCurrentTime(playerRef.current.currentTime);
  };

  const handleOnEnd = () => {
    setIsPlaying(false);
    handlePlayEndCb?.();
  };

  const handleOnDurationChange = () => {
    setDuration(playerRef.current.duration);
  };

  const handleLoadedMetadata = () => {
    setDuration(playerRef.current.duration);
  };

  const handleOnCanPlay = () => {
    setLoading(false);
  };

  const handleOnPlay = () => {
    setIsPlaying(true);

    // 如果 AudioContext 被 suspend，恢复它
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current
        .resume()
        .catch((err) => console.error('Failed to resume AudioContext on play:', err));
    }
  };

  const handleOnPause = () => {
    setIsPlaying(false);

    // 暂停时可以挂起 AudioContext
    if (audioContextRef.current?.state === 'running') {
      audioContextRef.current
        .suspend()
        .catch((err) => console.error('Failed to suspend AudioContext on pause:', err));
    }
  };

  const handleOnWaiting = () => {
    setLoading(true);
  };

  const handleOnRateChange = () => {
    setSpeed(playerRef.current.playbackRate.toFixed(2).toString());
  };

  // 初始化 AudioContext + 分析链路（只对 analyserAudioRef）
  const initAudioAnalysisGraph = () => {
    if (audioContextRef.current || !analyserAudioRef.current) return;

    const AudioCtx = (window.AudioContext ||
      (window as any).webkitAudioContext) as typeof AudioContext;
    const ctx = new AudioCtx();
    const analyser = ctx.createAnalyser();

    analyser.fftSize = 2048;

    const src = ctx.createMediaElementSource(analyserAudioRef.current);

    // 使用 GainNode 静音，但保证 analyser 前是完整信号
    const gain = ctx.createGain();

    gain.gain.value = 0; // 静音输出

    src.connect(analyser);
    analyser.connect(gain);
    gain.connect(ctx.destination);

    audioContextRef.current = ctx;
    analyserRef.current = analyser;
  };

  // 开启音量分析循环（RAF）
  const startVolumeLoop = () => {
    const analyser = analyserRef.current;

    if (!analyser) return;

    const dataArray = new Uint8Array(analyser.fftSize);
    let lastSampleTime = 0;
    const sampleInterval = 100; // 10Hz 采样

    const tick = (now: number) => {
      if (!analyserRef.current) return;

      if (!lastSampleTime || now - lastSampleTime >= sampleInterval) {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;

        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;

          sum += val * val;
        }

        const rms = Math.sqrt(sum / dataArray.length);
        const maxVolume = 0.2;

        if (rms > maxVolume) {
          setAudioVolume(1);
        } else {
          setAudioVolume(Math.min(1, Math.max(0, rms / maxVolume)));
        }

        lastSampleTime = now;
      }

      animationRef.current = requestAnimationFrame(tick);
    };

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    animationRef.current = requestAnimationFrame(tick);
  };

  // 启动分析（确保 analyserAudio 正在播放）
  const startAudioAnalysis = async () => {
    // 只有需要音量回调时才启用分析
    if (!handleVolumeChangeCb) return;

    if (!analyserAudioRef.current) return;

    initAudioAnalysisGraph();

    const ctx = audioContextRef.current;

    if (!ctx || !analyserRef.current) return;

    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (err) {
        console.error('Failed to resume AudioContext:', err);
      }
    }

    // 同步分析用 audio 的时间 & 倍速
    analyserAudioRef.current.currentTime = playerRef.current.currentTime;
    analyserAudioRef.current.playbackRate = playerRef.current.playbackRate;
    analyserAudioRef.current.defaultPlaybackRate = playerRef.current.playbackRate;

    try {
      await analyserAudioRef.current.play();
    } catch (err) {
      // 分析音频播放失败，不影响主播放
      console.warn('analyserAudio play failed:', err);
    }

    startVolumeLoop();
  };

  // -----------------------
  // 对外控制方法
  // -----------------------

  const handleSeekTime = (time: number) => {
    const safeTime = Math.min(duration, Math.max(0, time));

    playerRef.current.currentTime = safeTime;

    // 只有启用音量分析时才同步分析音频
    if (handleVolumeChangeCb) {
      analyserAudioRef.current.currentTime = safeTime;
    }

    handleSeekTimeCb?.(safeTime);
  };

  const handleReplay = () => {
    playerRef.current.currentTime = 0;

    if (handleVolumeChangeCb) {
      analyserAudioRef.current.currentTime = 0;
    }

    playerRef.current.play().catch((err) => console.error('Replay play failed:', err));
    startAudioAnalysis();
  };

  const handlePlayStatusChange = () => {
    if (lockPlayRef.current) return;

    if (isPlaying) {
      // 当前在播 -> 执行暂停
      playerRef.current.pause();

      if (handleVolumeChangeCb) {
        analyserAudioRef.current.pause();
      }

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      if (audioContextRef.current?.state === 'running') {
        audioContextRef.current
          .suspend()
          .catch((err) => console.error('Failed to suspend AudioContext:', err));
      }
    } else {
      // 当前暂停 -> 执行播放
      const playPromise = playerRef.current.play();
      const isPromise = playPromise instanceof Promise;

      if (isPromise) {
        lockPlayRef.current = true;
        const resetLock = () => {
          lockPlayRef.current = false;
        };

        playPromise.then(
          () => {
            resetLock();
            startAudioAnalysis();
          },
          () => {
            resetLock();
          }
        );
      } else {
        startAudioAnalysis();
      }
    }
  };

  const handleSpeedChange = (value: string) => {
    const rate = Math.min(2, Math.max(0.5, Number(value) || 1)); // 限制到 0.5~2 之间

    playerRef.current.defaultPlaybackRate = rate;
    playerRef.current.playbackRate = rate;

    // 只有启用音量分析时才同步分析音频
    if (handleVolumeChangeCb) {
      // 先同步时间，避免切换倍速时的微小偏差导致卡顿
      analyserAudioRef.current.currentTime = playerRef.current.currentTime;
      analyserAudioRef.current.defaultPlaybackRate = rate;
      analyserAudioRef.current.playbackRate = rate;
    }
  };

  // -----------------------
  // 各种副作用
  // -----------------------

  // 初始化 audio 元素 & 事件监听
  useEffect(() => {
    const player = playerRef.current;
    const analyserAudio = analyserAudioRef.current;

    const rate = parseFloat(defaultSpeed);

    player.defaultPlaybackRate = rate;
    player.playbackRate = rate;
    player.muted = muted;
    player.volume = volume;
    player.autoplay = autoPlay;
    player.crossOrigin = 'anonymous';

    analyserAudio.crossOrigin = 'anonymous';

    player.addEventListener('timeupdate', handleOnTimeUpdate);
    player.addEventListener('ended', handleOnEnd);
    player.addEventListener('durationchange', handleOnDurationChange);
    player.addEventListener('loadedmetadata', handleLoadedMetadata);
    player.addEventListener('canplay', handleOnCanPlay);
    player.addEventListener('play', handleOnPlay);
    player.addEventListener('pause', handleOnPause);
    player.addEventListener('waiting', handleOnWaiting);
    player.addEventListener('ratechange', handleOnRateChange);

    return () => {
      player.removeEventListener('timeupdate', handleOnTimeUpdate);
      player.removeEventListener('ended', handleOnEnd);
      player.removeEventListener('durationchange', handleOnDurationChange);
      player.removeEventListener('loadedmetadata', handleLoadedMetadata);
      player.removeEventListener('canplay', handleOnCanPlay);
      player.removeEventListener('play', handleOnPlay);
      player.removeEventListener('pause', handleOnPause);
      player.removeEventListener('waiting', handleOnWaiting);
      player.removeEventListener('ratechange', handleOnRateChange);

      player.pause();
      analyserAudio.pause();

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close().catch((err) => {
          console.error('Failed to close AudioContext:', err);
        });
        audioContextRef.current = null;
      }

      analyserRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 音量变化回调
  useEffect(() => {
    handleVolumeChangeCb?.(audioVolume);
  }, [audioVolume, handleVolumeChangeCb]);

  // 播放状态回调
  useEffect(() => {
    handlePlayStatusChangeCb?.(isPlaying);
  }, [isPlaying, handlePlayStatusChangeCb]);

  // 页面从后台回到前台时，若正在播放则恢复 AudioContext
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        audioContextRef.current?.state === 'suspended' &&
        isPlaying
      ) {
        audioContextRef.current
          .resume()
          .catch((err) =>
            console.error('Failed to resume AudioContext on visibility change:', err)
          );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying]);

  // link 变化时，重置两个 audio 的 src
  useEffect(() => {
    const player = playerRef.current;
    const analyserAudio = analyserAudioRef.current;

    player.src = link;

    // 只有启用音量分析时才加载分析音频
    if (handleVolumeChangeCb) {
      analyserAudio.src = link;
      analyserAudio.load();
    }

    // 重新加载
    player.load();

    setCurrentTime(0);
    setDuration(originDuration);
    setIsPlaying(false);
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, originDuration]);

  return {
    audioRef, // 播放用 audio
    handleSeekTime,
    handlePlayStatusChange,
    handleSpeedChange,
    handleReplay,
    isPlaying,
    currentTime,
    speed,
    duration,
    loading,
    audioVolume
  };
}

export default useAudio;
